import React, { useState } from "react";
import styled from "styled-components";
import { AlertTriangle, Trash2 } from "lucide-react";
import { Modal } from "../../../components/ui";
import { useTicketsStore } from "./use-tickets-store";
import { getStatusText } from "./tickets-types";
import * as api from "./tickets-api";

// ─── Styles ───────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  padding: 8px;
`;

const WarningRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const WarningIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #ffebee;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const WarningTitle = styled.h2`
  font-size: 17px;
  font-weight: 700;
  color: #202020;
  margin: 0;
`;

const WarningSubtitle = styled.div`
  font-size: 13px;
  color: #666;
  margin-top: 2px;
`;

const InfoCard = styled.div`
  padding: 14px;
  background: #fafafa;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #202020;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 8px;
  & + & {
    margin-top: 6px;
  }
`;

const InfoLabel = styled.span`
  color: #999;
  white-space: nowrap;
`;

const ReasonLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #202020;
  margin-bottom: 6px;
`;

const ReasonInput = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 13px;
  outline: none;
  margin-bottom: 20px;
  &:focus {
    border-color: #f44336;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const CancelBtn = styled.button`
  padding: 8px 20px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: white;
  color: #666;
  font-size: 13px;
  cursor: pointer;
  &:hover {
    background: #fafafa;
  }
`;

const ConfirmDeleteBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  border: none;
  border-radius: 6px;
  background: #f44336;
  color: white;
  font-size: 13px;
  cursor: pointer;
  &:hover {
    background: #d32f2f;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

export const DeleteTicketModal: React.FC = () => {
  const { selectedTicket, closeDeleteModal, fetch, fetchStats } =
    useTicketsStore();
  const [reason, setReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  if (!selectedTicket) return null;

  const handleDelete = async () => {
    if (!reason.trim()) return;
    setDeleting(true);
    try {
      await api.deleteTicket(selectedTicket.uuid, reason.trim());
      closeDeleteModal();
      fetch();
      fetchStats();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal handleClose={closeDeleteModal} dialogStyle={{ maxWidth: 480 }}>
      <Wrap>
        <WarningRow>
          <WarningIconWrap>
            <AlertTriangle size={22} color="#f44336" />
          </WarningIconWrap>
          <div>
            <WarningTitle>Удалить обращение</WarningTitle>
            <WarningSubtitle>
              Это действие можно отменить через восстановление
            </WarningSubtitle>
          </div>
        </WarningRow>

        <InfoCard>
          <InfoRow>
            <InfoLabel>Тема:</InfoLabel>
            <span style={{ fontWeight: 500 }}>{selectedTicket.title}</span>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Статус:</InfoLabel>
            <span>{getStatusText(selectedTicket.status)}</span>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Автор:</InfoLabel>
            <span>
              {selectedTicket.author_login || selectedTicket.author_email}
            </span>
          </InfoRow>
        </InfoCard>

        <ReasonLabel>Причина удаления *</ReasonLabel>
        <ReasonInput
          type="text"
          placeholder="Укажите причину удаления..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          autoFocus
        />

        <ButtonRow>
          <CancelBtn onClick={closeDeleteModal}>Отмена</CancelBtn>
          <ConfirmDeleteBtn
            onClick={handleDelete}
            disabled={!reason.trim() || deleting}
          >
            <Trash2 size={14} />
            Удалить
          </ConfirmDeleteBtn>
        </ButtonRow>
      </Wrap>
    </Modal>
  );
};
